import { Button, Grid, Stack } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import {
  buildSaveStatusCheckRequest,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { useCreateStatusCheck } from 'queries/status-checks/use-create-status-check'
import { useUpdateStatusCheck } from 'queries/status-checks/use-update-status-check'
import {
  ObservabilityToolType,
  type ServiceStatusCheckFromStatusCheck
} from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

/**
 * These fields don't require re-testing by the user for the form to be considered valid.
 * Manual re-validation (via the user testing the connection and evaluation sections)
 * is required for any fields in the monitor/alert and success criteria sections.
 */
const FIELDS_THAT_DONT_REQUIRE_RETEST: Set<keyof StatusCheckFormValues> =
  new Set(['category', 'pollingIntervalSeconds', 'statusCheckName'])

const SaveStatusCheckButton: React.FC = () => {
  const { formState, watch } = useFormContext<StatusCheckFormValues>()
  const { dirtyFields, isDirty, isValid, isSubmitting } = formState

  const dirtyFieldsKeys = Object.keys(dirtyFields)

  const hasOnlyChangesThatDontNeedRetest =
    isDirty &&
    dirtyFieldsKeys.every((dirtyField: keyof StatusCheckFormValues) =>
      FIELDS_THAT_DONT_REQUIRE_RETEST.has(dirtyField)
    )

  const { isStatusCheckConnectionValidated, isStatusCheckEvaluationValidated } =
    useStatusCheckDetailsContext()

  const selectedObservabilityToolType = watch('observabilityTool.type')

  // Should only validate the Success Evaluation when the observability tool type is custom
  const isSuccessEvaluationValid =
    selectedObservabilityToolType === ObservabilityToolType.CUSTOM
      ? isStatusCheckEvaluationValidated
      : true

  const canSaveForm =
    isDirty &&
    isValid &&
    !isSubmitting &&
    (hasOnlyChangesThatDontNeedRetest ||
      (isSuccessEvaluationValid && isStatusCheckConnectionValidated))

  return (
    <Button
      key="formSubmit"
      data-testid="edit-status-check-save-button"
      disabled={!canSaveForm}
      sx={{ width: 'fit-content' }}
      type="submit"
      variant="contained"
    >
      Save Health Check
    </Button>
  )
}

export const EditStatusCheckForm: React.FC<{
  headerContent?: React.ReactNode
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ headerContent, statusCheck }) => {
  const isClone = statusCheck.id === null
  const location = useLocation()
  const navigate = useNavigate()
  const statusChecksUrls = getStatusChecksUrls()
  const { currentTeamId } = useAuth()
  const { handleSubmit } = useFormContext<StatusCheckFormValues>()
  const { referrer = statusChecksUrls.base() } = location.state ?? {}
  const { refetch: reFetchStatusChecks } = useStatusChecksContext()
  const { mutateAsync: createStatusCheck } = useCreateStatusCheck()
  const { mutateAsync: updateStatusCheck } = useUpdateStatusCheck(
    statusCheck.id
  )

  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const request = await buildSaveStatusCheckRequest(values, currentTeamId)

      await (isClone ? createStatusCheck(request) : updateStatusCheck(request))

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: ms.seconds(2)
      })

      // Refresh status checks list
      await handleDataFetchOnChange(reFetchStatusChecks)

      // Navigate to back to status check list
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
      <Grid container>
        <Grid item lg={9} xs={12}>
          <Stack sx={{ rowGap: 3 }}>
            {headerContent}
            <StatusCheckConnectionForm />
            <Stack direction="row" sx={{ columnGap: 2 }}>
              <SaveStatusCheckButton />
              <Button
                color="secondary"
                component={StyledRouterLink}
                to={referrer === '/' ? statusChecksUrls.list() : referrer}
                variant="contained"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
